import { useContext, useMemo } from 'react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { GlobalUtilContext } from '../../core/contexts';
import NumericalInfoWithDelta from './components/NumericalInfo';
import PieChart from './components/PieChart';
import BarChart from './components/BarChart';
import StackedBar from './components/StackedBar';
import { useTranslation } from 'react-i18next';
import {
  CAT_VS_UNCAT_COLOR_MAPPING,
  IP_TO_URL_COLOR_MAPPING,
  STATUS_TO_COLOR_MAPPING
} from '../../core/layouts/constants';

export function Telemetry() {
  const { t } = useTranslation();
  const { analyticsData, leaderboard, selectedService, isLeaderboardLoading } =
    useContext(GlobalUtilContext);

  const displayedMessage = useMemo(() => {
    if (!selectedService) {
      return t('telemetry.selectService');
    }

    if (analyticsData?.error) {
      return t(`telemetry.${analyticsData.message}`);
    }

    return null;
  }, [selectedService, analyticsData]);

  return !displayedMessage ? (
    <Box
      sx={{
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'space-between',
          '@media (max-width: 1040px)': {
            flexWrap: 'wrap'
          }
        }}
      >
        <NumericalInfoWithDelta
          sx={{
            flex: 1,
            '@media (max-width: 1040px)': {
              minWidth: '256px',
              minHeight: '256px'
            }
          }}
          data={analyticsData?.total}
          valueKey={'value'}
          name={'total'}
        />
        <NumericalInfoWithDelta
          sx={{
            flex: 1,
            '@media (max-width: 1040px)': {
              minWidth: '256px',
              minHeight: '256px'
            }
          }}
          data={analyticsData?.total_cat}
          valueKey={'value'}
          name={'total_cats'}
        />
        <NumericalInfoWithDelta
          sx={{
            flex: 1,
            '@media (max-width: 1040px)': {
              minWidth: '256px',
              minHeight: '256px'
            }
          }}
          data={analyticsData?.total_uncat}
          valueKey={'value'}
          name={'total_uncats'}
        />
        <PieChart
          title={'telemetry.pieChart'}
          sx={{ flex: 2 }}
          data={analyticsData?.get_cat_vs_uncat || []}
          colorMapping={CAT_VS_UNCAT_COLOR_MAPPING}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <StackedBar
          sx={{ flex: 4 }}
          analytics={analyticsData?.serialized_metadatas_region}
          title={'regions'}
        />
        <PieChart
          title={'telemetry.pieChart2'}
          sx={{ flex: 2 }}
          data={analyticsData?.get_total_distributed_across_regions || []}
          colorMapping={STATUS_TO_COLOR_MAPPING}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <StackedBar
          sx={{ flex: 4, position: 'relative' }}
          analytics={analyticsData?.serialized_metadatas_partner}
          title={'partner'}
          isLarge
        />
        <BarChart
          sx={{ flex: 2 }}
          isHorizontal
          data={leaderboard}
          title={'leaderboard'}
          loading={isLeaderboardLoading}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <PieChart
          sx={{ flex: 2 }}
          data={analyticsData?.get_ip_vs_url || []}
          colorMapping={IP_TO_URL_COLOR_MAPPING}
          title={'telemetry.ip'}
        />
      </Box>
    </Box>
  ) : (
    <Typography
      sx={{
        fontFamily: 'Mulish',
        fontSize: '18px',
        fontWeight: '600',
        color: 'grey',
        textTransform: 'uppercase',
        textAlign: 'center',
        px: 2,
        my: 'auto',
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-37%)'
      }}
    >
      {displayedMessage}
    </Typography>
  );
}
